/**
 * The Finance Kanban's model (Isaiah Cotto, 2026-10-13; docs/design/kanban.md): pure data and functions, shared by
 * the board, its card panel and the tests.
 *
 * A card is ONE HANDOFF (a board row of kind HANDOFF), never a resident. Its column is the server's state
 * (`emerge_state.key`, financeBoard.js STATES) grouped into seven columns; nothing here derives a state from dates,
 * money or any other field.
 *
 *   New handoff         not-received + received
 *   Verification        accepted                                  (key `accepted`)
 *   Ready to process    invoiced + terms-recorded                 (key `invoicing`)
 *   Processing          partly-paid + written-off + collecting    (key `collecting`)
 *   Paid in full        paid-in-full
 *   Closed              closed            (terminal)
 *   Returned            returned          (terminal; to UMED, or a private-pay handoff to the Patient Advocate)
 *
 * Not columns: "Approved, no Finance handoff" (held) and the legacy Monday lens -- the Elsewhere strip counts them
 * and links to their tabs. No dispute or failed-settlement column exists, because no such state exists.
 *
 * Isaiah Cotto, 2026-10-14: Verification, Ready to process and Processing are Finance's NAMES for three existing
 * columns (formerly Accepted, Invoicing & terms, Collecting) -- a rename over the same states, not new tracked states:
 * the keys and the drop map are unchanged, and eMerge records no verification step of its own. The server's column
 * labels (financeKanban.js COLUMNS) are the ones drawn; these mirror them.
 *
 * A drop never writes. FINANCE_TRANSITIONS says which drops OPEN which governed act (the card panel then opens the
 * act's own drawer, against the card's own handoff); everything else is refused with one line.
 */
import type { TransitionMap, KanbanCardBase, KanbanChip, InPlaceAct } from '../../components/board/kanban';
import type { FinanceBoardRow, FinanceStateKey } from './boardApi';
import type { HandoffWorkAct } from './HandoffWork';
import { actsFor } from './HandoffWork';
import type { ResidentContext } from './financeApi';
import { correctedWord } from './financeApi';

export type FinanceColumnKey = 'new' | 'accepted' | 'invoicing' | 'collecting' | 'paid' | 'closed' | 'returned';

export interface FinanceColumnDef { key: FinanceColumnKey; label: string; states: FinanceStateKey[]; what: string }

/** The seven columns, each the server states it holds. The order of `states` is only a default: the server's tab order wins. */
export const FINANCE_COLUMNS: FinanceColumnDef[] = [
  { key: 'new', label: 'New handoff', states: ['not-received', 'received'], what: 'Handed to Finance and not accepted yet: not received in eMerge, or received and waiting to be accepted.' },
  { key: 'accepted', label: 'Verification', states: ['accepted'], what: 'Accepted by Finance, which is checking the record before invoicing. No invoice and no terms are recorded yet. eMerge records the acceptance; the check itself is not a recorded step.' },
  { key: 'invoicing', label: 'Ready to process', states: ['invoiced', 'terms-recorded'], what: 'One of the two is recorded: the invoice (terms owed) or the terms (invoice owed). Processing starts once both are recorded.' },
  { key: 'collecting', label: 'Processing', states: ['partly-paid', 'written-off', 'collecting'], what: 'Invoice and terms are both recorded: no payment recorded yet, partly paid, or written off and not closed.' },
  { key: 'paid', label: 'Paid in full', states: ['paid-in-full'], what: 'Finance recorded the balance paid in full and has not closed the handoff.' },
  { key: 'closed', label: 'Closed', states: ['closed'], what: 'Finance closed the handoff. Final.' },
  { key: 'returned', label: 'Returned', states: ['returned'], what: 'Sent back: a UMED handoff to UMED, a private-pay handoff to the Patient Advocate. Final here.' },
];
export const FINANCE_COLUMN_ORDER: FinanceColumnKey[] = FINANCE_COLUMNS.map((c) => c.key);
/** Every state a column holds, in the column order: the eleven tabs the board reads, one request each. */
export const KANBAN_STATES: FinanceStateKey[] = FINANCE_COLUMNS.flatMap((c) => c.states);

/** The column that holds a server state; null for anything else (held, monday-only, an unknown key). */
export function columnOfState(key: string | null | undefined): FinanceColumnKey | null {
  const c = FINANCE_COLUMNS.find((x) => (x.states as string[]).includes(String(key)));
  return c ? c.key : null;
}

/** The short word a card says for its state, so a column holding two or three states says which. */
export const STATE_WORD: Record<FinanceStateKey, string> = {
  'not-received': 'Not received yet',
  received: 'Received, not accepted',
  accepted: 'Accepted',
  invoiced: 'Invoice recorded, terms owed',
  'terms-recorded': 'Terms recorded, invoice owed',
  'partly-paid': 'Partly paid',
  'written-off': 'Written off, not closed',
  collecting: 'No payment recorded',
  'paid-in-full': 'Paid in full, not closed',
  closed: 'Closed',
  returned: 'Returned',
};

export interface FinanceCard extends KanbanCardBase {
  column: FinanceColumnKey;
  /** The server's state key (emerge_state.key). */
  state: FinanceStateKey;
  kind: 'UMED' | 'PRIVATE_PAY';
  /** null until Finance receives a UMED handoff. */
  handoffId: string | null;
  version: number | null;
  umedActionId: string | null;
  residentId: string | null;
  name: string;
  row: FinanceBoardRow;
}

/** A board row as a card, or null when it is not a handoff the columns hold. The row's own id is the card's id. */
export function cardFromRow(row: FinanceBoardRow): FinanceCard | null {
  if (row.kind !== 'HANDOFF' || !row.handoff || !row.emerge_state) return null;
  // GET /finance/kanban places the card (row.kanban.column); the same state table decides a /board row. Never a field.
  const placed = row.kanban && (FINANCE_COLUMN_ORDER as string[]).includes(row.kanban.column) ? (row.kanban.column as FinanceColumnKey) : null;
  const column = placed || columnOfState(row.emerge_state.key);
  if (!column) return null;
  return {
    id: row.id,
    column,
    state: row.emerge_state.key as FinanceStateKey,
    kind: row.handoff.kind === 'PRIVATE_PAY' ? 'PRIVATE_PAY' : 'UMED',
    handoffId: row.handoff.handoff_id,
    version: row.handoff.version,
    umedActionId: row.handoff.umed_action_id,
    residentId: row.resident ? row.resident.id : null,
    name: row.resident ? row.resident.name : 'No resident linked',
    row,
  };
}

/** The state word on a returned card: where it went. */
export const returnedWord = (card: Pick<FinanceCard, 'kind'>) => (card.kind === 'PRIVATE_PAY' ? 'Returned to the Patient Advocate' : 'Sent back to UMED');

const shortDay = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/** At most two chips, most serious first: facts Finance should see before acting. Never a time-derived status. */
export function chipsFor(row: FinanceBoardRow): KanbanChip[] {
  const out: KanbanChip[] = [];
  const h = row.handoff;
  if (h && h.attention === 'UMED_OBLIGATION_CANCELLED') out.push({ key: 'umed-cancelled', label: 'UMED cancelled its obligation', tone: 'stop' });
  if (row.deceased_reported) out.push({ key: 'deceased', label: `Deceased reported${row.deceased_reported.effective_date ? ` ${shortDay(row.deceased_reported.effective_date)}` : ''}`, tone: 'warn' });
  // CORRECTIONS (2026-10-28): the chip says the correction's state once the server does; the card itself never moves for it.
  if (h && h.awaiting_correction) out.push({ key: 'awaiting-correction', label: h.awaiting_correction.state_words || 'Months await a correction', tone: 'warn' });
  if (h && h.corrected && h.corrected.count > 0) out.push({ key: 'corrected', label: `${correctedWord(h.corrected)}${h.corrected.last_on ? ` ${shortDay(h.corrected.last_on)}` : ''}`, tone: 'info' });
  if (row.flags && row.flags.count > 0) out.push({ key: 'flag', label: row.flags.latest.label, tone: 'info' });
  return out.slice(0, 2);
}

/* ── Drops ─────────────────────────────────────────────────────────── */

/**
 * What a drop's availability check is given: the viewer's capabilities (scope.can). `canMarkAtd` (2026-10-15): the
 * ATD command is open to this viewer (GET /atd/meta can.mark: RESIDENT_ATD_MARK with ATD writes open). Optional, so a
 * board that never asked offers no "Mark ATD…".
 */
export interface FinanceKanbanCtx { canManage: boolean; canSendBack: boolean; canMarkAtd?: boolean }

/**
 * The board's own act ids: what onAct hands the card panel, which resolves them against the handoff it reads again.
 * K10 (2026-10-14): a column pair may name the card's own act, so Move… and the status line say "Receive" or
 * "Accept", "Record terms" or "Record invoice" -- never "Receive or accept". Only Verification -> Ready to process (and
 * -> Processing) is a genuine choice of two acts, and says so.
 */
export type FinanceBoardAct = 'receive' | 'accept' | 'invoice-or-terms' | 'invoice' | 'terms' | 'payment-paid' | 'close' | 'close-accepted' | 'send-back' | 'return-to-pa' | 'mark-atd';

/**
 * The card's resident's ATD word as the board row carries it (null: blank, or a row read before ATD was on it).
 * Never decides a column: Finance's columns are handoff states, and no ATD column exists.
 */
export const atdWordOf = (card: Pick<FinanceCard, 'row'>): string | null => {
  const a = card.row.atd;
  return a && a.status && a.status.trim() ? a.status.trim() : null;
};
/** What the card says for ATD: "Yes · since Oct 15", "Dispensed", "No", or null (blank: the card says "not recorded"). */
export function atdCardWords(row: FinanceBoardRow): string | null {
  const a = row.atd;
  if (!a || !a.status || !a.status.trim()) return null;
  const word = a.status.trim();
  const since = word.toUpperCase() === 'YES' ? shortDay(a.atd_date) : null;
  return since ? `${word} · since ${since}` : word;
}
/** Words that already mean "marked": Mark ATD is not offered on these cards. */
const ATD_MARKED = new Set(['YES', 'DISPENSED']);

/**
 * "Mark ATD…" (Isaiah Cotto, 2026-10-15): an act the card takes where it stands. It OPENS the card's dock at the ATD
 * panel's confirmation for THE CARD'S resident (card.residentId); nothing is recorded until its own confirm. Offered
 * when the viewer may mark ATD and the row does not already say Yes or Dispensed (the panel re-reads and says
 * "already marked" if the row was stale). Independent of manage_handoff: ATD is RESIDENT_ATD_MARK's.
 */
export function atdInPlace(card: FinanceCard, ctx: FinanceKanbanCtx): InPlaceAct[] {
  if (!ctx.canMarkAtd || !card.residentId) return [];
  const word = atdWordOf(card);
  if (word && ATD_MARKED.has(word.toUpperCase())) return [];
  return [{ act: 'mark-atd', label: 'Mark ATD…', opens: `Opens Mark ATD for ${card.name}, with its confirmation`, available: true }];
}

export const READ_ONLY_REASON = 'Read-only: handoff acts need the Finance handoff permission, with handoff writes open.';
const ro = (ctx: FinanceKanbanCtx): string | null => (ctx.canManage ? null : READ_ONLY_REASON);
const owed = (card: FinanceCard): 'terms' | 'invoice' => (card.state === 'invoiced' ? 'terms' : 'invoice');
const backTo = (card: FinanceCard): 'return-to-pa' | 'send-back' => (card.kind === 'PRIVATE_PAY' ? 'return-to-pa' : 'send-back');

export const FINANCE_TRANSITIONS: TransitionMap<FinanceCard, FinanceKanbanCtx> = {
  inPlace: atdInPlace,
  moves: {
    new: {
      accepted: {
        kind: 'act',
        act: (card) => (card.state === 'received' ? 'accept' : 'receive'),
        label: (card) => (card.state === 'received' ? 'Accept…' : 'Receive…'),
        opens: (card) => (card.state === 'received' ? 'Opens Accept, with its confirmation' : 'Opens Receive, the first of two acts: accepting comes after it'),
        available: (card, ctx) => ro(ctx) || (card.state === 'received' ? true
          : card.kind === 'PRIVATE_PAY' ? 'A private-pay handoff arrives already received.'
            : !card.umedActionId ? 'This row names no UMED obligation to receive.' : true),
      },
      returned: {
        kind: 'act', act: 'return-to-pa', label: 'Return to the Patient Advocate…', opens: 'Opens the return to the Patient Advocate, with its reason',
        available: (card, ctx) => ro(ctx) || (card.state === 'not-received' ? 'Finance has not received this handoff, so there is nothing to send back; UMED still holds it.'
          : card.kind !== 'PRIVATE_PAY' ? 'A UMED handoff goes back by the signed send-back, once you have accepted it.' : true),
      },
      invoicing: { kind: 'refuse', reason: (card) => (card.state === 'not-received' ? 'One act at a time: receive it, then accept it, before anything is invoiced.' : 'Accept it first: nothing is invoiced on a handoff Finance has not accepted.') },
      collecting: { kind: 'refuse', reason: (card) => (card.state === 'not-received' ? 'One act at a time: receive it, then accept it; Processing needs the invoice and the terms after that.' : 'Accept it first; Processing needs the invoice and the terms after that.') },
      paid: { kind: 'refuse', reason: 'Paid in full is recorded from Processing, after acceptance, the invoice and the terms.' },
      closed: { kind: 'refuse', reason: (card) => (card.state === 'not-received' ? 'Finance has not received this handoff; receive it first.' : 'Accept it first. An accepted handoff can be closed without collection, with a reason.') },
    },
    accepted: {
      invoicing: {
        kind: 'act', act: 'invoice-or-terms', label: 'Record invoice or terms…', opens: 'Opens a choice: Record invoice or Record terms, whichever comes first',
        available: (_card, ctx) => ro(ctx) || true,
      },
      collecting: {
        kind: 'act', act: 'invoice-or-terms', label: 'Start processing (two acts)…', opens: 'Processing needs two acts, the invoice and the terms: opens the choice of the first',
        available: (_card, ctx) => ro(ctx) || true,
      },
      paid: { kind: 'refuse', reason: 'Paid in full is recorded from Processing, once the invoice and the terms are both recorded.' },
      closed: {
        kind: 'act', act: 'close-accepted', label: 'Close without collection…', opens: 'Opens Close without collection: a reason is required, and it skips invoicing',
        available: (_card, ctx) => ro(ctx) || true,
      },
      returned: {
        kind: 'act',
        act: backTo,
        label: (card) => (card.kind === 'PRIVATE_PAY' ? 'Return to the Patient Advocate…' : 'Send back to UMED…'),
        opens: (card) => (card.kind === 'PRIVATE_PAY' ? 'Opens the return to the Patient Advocate, with its reason' : 'Opens the signed send-back to UMED: reason and authenticator code'),
        available: (card, ctx) => ro(ctx) || (card.kind !== 'PRIVATE_PAY' && !ctx.canSendBack ? 'The signed send-back to UMED is not open for you.' : true),
      },
    },
    invoicing: {
      collecting: {
        kind: 'act',
        act: owed,
        label: (card) => (owed(card) === 'terms' ? 'Record terms…' : 'Record invoice…'),
        opens: (card) => (owed(card) === 'terms' ? 'Opens Record terms: the invoice is recorded, the terms are owed' : 'Opens Record invoice: the terms are recorded, the invoice is owed'),
        available: (_card, ctx) => ro(ctx) || true,
      },
      paid: { kind: 'refuse', reason: (card) => `Paid in full is recorded from Processing: record the ${owed(card)} first.` },
      closed: { kind: 'refuse', reason: 'Once an invoice or terms are recorded, the handoff is collected before it closes.' },
      returned: { kind: 'refuse', reason: 'Once an invoice or terms are recorded, the handoff is not sent back.' },
    },
    collecting: {
      paid: {
        kind: 'act', act: 'payment-paid', label: 'Record paid in full…', opens: 'Opens Record payment state, set to Paid in full (no amount)',
        available: (_card, ctx) => ro(ctx) || true,
      },
      closed: {
        kind: 'act', act: 'close', label: 'Close the written-off handoff…', opens: 'Opens Close handoff: a reason is required',
        available: (card, ctx) => ro(ctx) || (card.state === 'written-off' ? true : 'Only a written-off balance closes from Processing: record it paid in full, or written off, first.'),
      },
      returned: { kind: 'refuse', reason: 'A handoff in Processing is not sent back: it is paid in full or written off, then closed.' },
    },
    paid: {
      closed: {
        kind: 'act', act: 'close', label: 'Close…', opens: 'Opens Close handoff',
        available: (_card, ctx) => ro(ctx) || true,
      },
      returned: { kind: 'refuse', reason: 'A paid handoff is not sent back: close it.' },
    },
  },
  fallbackReason: (from, to) => {
    if (from === 'closed') return 'A closed handoff is final: nothing moves out of Closed.';
    if (from === 'returned') return 'A returned handoff is final here: nothing moves out of Returned.';
    const a = FINANCE_COLUMN_ORDER.indexOf(from as FinanceColumnKey);
    const b = FINANCE_COLUMN_ORDER.indexOf(to as FinanceColumnKey);
    if (b >= 0 && a >= 0 && b < a) return 'Handoffs do not move backward: each state is recorded once, and eMerge refuses to undo it.';
    return 'No single act makes that move. Open the card to see its next act.';
  },
};

/* ── From a board act to the governed act, on the handoff the panel loaded ── */

type HandoffRecord = NonNullable<ResidentContext['handoff']>;

export type ResolvedAct =
  | { kind: 'work'; acts: HandoffWorkAct[]; payState?: 'PAID_IN_FULL'; prompt?: string }
  | { kind: 'receive' }
  | { kind: 'send-back' }
  | { kind: 'none'; why: string };

const NOT_NOW = (record: HandoffRecord | null) => `That act is not open for this handoff now${record ? ` (it is ${String(record.state).replace(/_/g, ' ').toLowerCase()})` : ''}. Nothing was opened.`;

/**
 * What a board act opens on THIS handoff, as the panel just read it (never the card's possibly stale state): the
 * acts offered by actsFor on the fresh record, intersected with what the move may open. A not-received card has no
 * record: its only act is Receive.
 */
export function resolveBoardAct(act: FinanceBoardAct, card: FinanceCard, record: HandoffRecord | null, can: { manage: boolean; sendBack: boolean }): ResolvedAct {
  if (!can.manage) return { kind: 'none', why: READ_ONLY_REASON };
  if (!record) {
    if (act === 'receive' && card.kind === 'UMED' && card.umedActionId) return { kind: 'receive' };
    return { kind: 'none', why: NOT_NOW(null) };
  }
  const offered = actsFor(record);
  const pick = (want: HandoffWorkAct[], extra: Partial<Extract<ResolvedAct, { kind: 'work' }>> = {}): ResolvedAct => {
    const acts = want.filter((a) => offered.includes(a));
    return acts.length ? { kind: 'work', acts, ...extra } : { kind: 'none', why: NOT_NOW(record) };
  };
  switch (act) {
    case 'receive': return { kind: 'none', why: `Finance already holds this handoff (it is ${String(record.state).replace(/_/g, ' ').toLowerCase()}). Nothing was opened.` };
    case 'accept': return pick(['accept']);
    case 'invoice': return pick(['invoice']);
    case 'terms': return pick(['terms']);
    case 'invoice-or-terms': return pick(['invoice', 'terms'], { prompt: 'Verification to Ready to process: record the invoice or the terms first. Processing starts once both are recorded.' });
    case 'payment-paid': return pick(['payment'], { payState: 'PAID_IN_FULL' });
    case 'close': return pick(['close']);
    case 'close-accepted': return pick(['close', 'invoiced_monday'], { prompt: 'A Monday Finance row is attached to this handoff. If Monday already invoiced it, record that instead of closing without collection.' });
    case 'return-to-pa': return record.kind === 'PRIVATE_PAY' ? pick(['return']) : { kind: 'none', why: NOT_NOW(record) };
    case 'send-back':
      if (record.kind === 'PRIVATE_PAY' || record.state !== 'ACCEPTED') return { kind: 'none', why: NOT_NOW(record) };
      return can.sendBack ? { kind: 'send-back' } : { kind: 'none', why: 'The signed send-back to UMED is not open for you.' };
    default: return { kind: 'none', why: NOT_NOW(record) };
  }
}
